import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { getTenantSettings } from '../api/tenantSettingsApi';
import { useAuth } from './AuthContext';
import type { EncounterFieldsConfig } from '../constants/encounterFieldsConfig';

const EMPTY_ENCOUNTER_FIELDS_CONFIG: EncounterFieldsConfig = { encounterFields: {}, claimTypeFields: {} };

/** Context state for tenant settings */
interface TenantSettingsState {
  /** Selected claim types for the tenant */
  claimTypes: string[];
  /** Two-tier form field config (feature 043). `{}` when never configured. */
  encounterFieldsConfig: EncounterFieldsConfig;
  /** Whether settings are currently being loaded */
  isLoading: boolean;
  /** Error message if load/save failed */
  error: string | null;
  /** Refresh settings from server */
  refresh: () => Promise<void>;
}

const TenantSettingsContext = createContext<TenantSettingsState | undefined>(
  undefined
);

interface TenantSettingsProviderProps {
  children: ReactNode;
}

/**
 * Provider for tenant settings.
 * Fetches settings on mount and provides methods to update them.
 */
export function TenantSettingsProvider({
  children,
}: TenantSettingsProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [claimTypes, setClaimTypes] = useState<string[]>([]);
  const [encounterFieldsConfig, setEncounterFieldsConfig] = useState<EncounterFieldsConfig>(EMPTY_ENCOUNTER_FIELDS_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tenantId = user?.tenantId;

  const fetchSettings = useCallback(async () => {
    if (!tenantId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const settings = await getTenantSettings(tenantId);
      setClaimTypes(settings.claimTypes);
      setEncounterFieldsConfig(settings.encounterFieldsConfig ?? EMPTY_ENCOUNTER_FIELDS_CONFIG);
    } catch (err) {
      console.error('Failed to fetch tenant settings:', err);
      setError('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  // Fetch settings when authenticated and tenant ID is available
  // Clear previous tenant's settings immediately on tenant change
  useEffect(() => {
    if (isAuthenticated && tenantId) {
      // Clear stale settings before fetching new tenant's settings
      setClaimTypes([]);
      setEncounterFieldsConfig(EMPTY_ENCOUNTER_FIELDS_CONFIG);
      fetchSettings();
    } else {
      setClaimTypes([]);
      setEncounterFieldsConfig(EMPTY_ENCOUNTER_FIELDS_CONFIG);
      setIsLoading(false);
    }
  }, [isAuthenticated, tenantId, fetchSettings]);

  const value: TenantSettingsState = {
    claimTypes,
    encounterFieldsConfig,
    isLoading,
    error,
    refresh: fetchSettings,
  };

  return (
    <TenantSettingsContext.Provider value={value}>
      {children}
    </TenantSettingsContext.Provider>
  );
}

/**
 * Hook to access tenant settings.
 * Must be used within a TenantSettingsProvider.
 */
export function useTenantSettings(): TenantSettingsState {
  const context = useContext(TenantSettingsContext);
  if (context === undefined) {
    throw new Error(
      'useTenantSettings must be used within a TenantSettingsProvider'
    );
  }
  return context;
}

export { TenantSettingsContext };
